import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getReport } from '../../api/sessions';

/**
 * Slide-over drawer showing details for a single pipeline card:
 *  - schedule meta (candidate, host, time, stage, status)
 *  - linked session id + scorecard recommendation (already on the card)
 *  - on demand: fetch the full session report for findings counts
 *
 * Closes on backdrop click or Esc.
 */
export default function PipelineDetailDrawer({ card, onClose }) {
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // ── Esc to close ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!card) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [card, onClose]);

  // ── Lazy-load report when a session is linked ────────────────────────────
  useEffect(() => {
    setReport(null);
    setError(null);
    if (!card?.sessionId) return;

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await getReport(card.sessionId);
        if (!cancelled) setReport(res.data || null);
      } catch (e) {
        if (!cancelled) {
          console.warn('[PipelineDetailDrawer] report fetch failed:', e.message);
          setError(e.response?.data?.message || 'Failed to load report');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [card?.sessionId]);

  if (!card) return null;

  const scheduledAt = card.scheduledAt
    ? new Date(card.scheduledAt).toLocaleString()
    : '-';

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 bg-slate-900/40"
      />

      {/* Panel */}
      <aside className="w-full max-w-md bg-white shadow-2xl border-l border-slate-200
                        flex flex-col h-full">
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {card.candidateName || 'Candidate'}
            </h2>
            <p className="text-xs text-slate-400">{card.candidateEmail}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 text-sm">
          {/* Schedule block */}
          <Section title="Interview">
            <Field label="Title"        value={card.title || '-'} />
            <Field label="Scheduled at" value={scheduledAt} />
            <Field label="Duration"     value={card.durationMinutes ? `${card.durationMinutes} min` : '-'} />
            <Field label="Stage"        value={card.stage || '-'} />
            <Field label="Status"       value={card.status || '-'} />
            <Field label="Host"         value={card.hostName || card.hostEmail || '-'} />
          </Section>

          {/* Session block */}
          <Section title="Linked session">
            {card.sessionId ? (
              <>
                <Field
                  label="Session"
                  value={
                    <Link
                      to={`/sessions/${card.sessionId}`}
                      className="text-teal-600 hover:text-teal-700 font-mono hover:underline break-all"
                      onClick={onClose}
                    >
                      {card.sessionId}
                    </Link>
                  }
                />
                <Field
                  label="Recommendation"
                  value={card.scorecardRecommendation || 'Not yet scored'}
                />
              </>
            ) : (
              <p className="text-slate-400 text-xs">No session has been linked to this schedule yet.</p>
            )}
          </Section>

          {/* Report block (lazy) */}
          {card.sessionId && (
            <Section title="Report summary">
              {loading && <p className="text-xs text-slate-400">Loading…</p>}
              {error   && <p className="text-xs text-rose-500">{error}</p>}
              {report  && (
                <>
                  <Field label="Integrity signals (unreviewed)" value={report.totalFindings ?? 0} />
                  {/* I-P0-B: server integrity status; the retired 0-100 risk numbers are not shown. */}
                  <Field label="Integrity status" value={
                    ({ NONE: 'No review signals', MEDIUM: 'Review signals (medium)', HIGH: 'Review signals (high)' })[
                      (report.integrityStatus || '').toUpperCase()] ?? 'Not evaluated'} />
                  <Link
                    to={`/reports/${card.sessionId}`}
                    onClick={onClose}
                    className="inline-block mt-2 text-teal-600 hover:text-teal-700 font-medium hover:underline text-xs"
                  >
                    View full report →
                  </Link>
                </>
              )}
              {!loading && !error && !report && (
                <p className="text-xs text-slate-400">No report available.</p>
              )}
            </Section>
          )}
        </div>
      </aside>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-700 text-right break-words">{value}</span>
    </div>
  );
}
